import React, { useState, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { OrderDetailsModal } from '../common/OrderDetailsModal';
import { DraggableRow } from './DraggableRow';

interface RouteJobsTableProps {
  jobs: any[];
  localOrder: string[]; // Array of job IDs in current order
  onOrderChange: (newOrder: string[]) => void;
  onJobUpdated: () => void;
  deliveryUsers: any[];
  selectedEngineerId?: string; // Pass the selected engineer ID
}

export const RouteJobsTable: React.FC<RouteJobsTableProps> = ({ 
  jobs, 
  localOrder,
  onOrderChange,
  onJobUpdated,
  deliveryUsers,
  selectedEngineerId
}) => {
  // Get the selected engineer's username
  const selectedEngineer = deliveryUsers.find(u => String(u.id) === selectedEngineerId);
  const selectedUsername = selectedEngineer?.username || '';
  const [viewOrdersModal, setViewOrdersModal] = useState<{ jobId: string; customerName: string } | null>(null);

  // Sort jobs according to local order
  const sortedJobs = useMemo(() => {
    const jobsMap = new Map(jobs.map(job => [job.id, job]));
    return localOrder.map(id => jobsMap.get(id)).filter(Boolean) as any[];
  }, [jobs, localOrder]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localOrder.indexOf(active.id as string);
      const newIndex = localOrder.indexOf(over.id as string);
      
      const newOrder = arrayMove(localOrder, oldIndex, newIndex);
      onOrderChange(newOrder);
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10">
                  {/* Drag handle column */}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Postcode</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Glass Arrival</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manufacture</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Date</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engineer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lock Notes</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Added</th>
              </tr>
            </thead>
            <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedJobs.map((job, index) => (
                  <DraggableRow
                    key={job.id}
                    job={job}
                    index={index}
                    onViewOrders={(jobId, customerName) => setViewOrdersModal({ jobId, customerName })}
                    onDateUpdated={onJobUpdated}
                    onUserUpdated={onJobUpdated}
                    deliveryUsers={deliveryUsers}
                    selectedUsername={selectedUsername}
                  />
                ))}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </div>

      {/* View Orders Modal */}
      {viewOrdersModal && (
        <OrderDetailsModal
          isOpen={true}
          onClose={() => setViewOrdersModal(null)}
          jobId={viewOrdersModal.jobId}
          title={`Orders - ${viewOrdersModal.customerName}`}
          mode="view"
        />
      )}
    </>
  );
};
